// A single- or double-quoted string literal. A backslash escapes the next character, so a role key may contain either
// quote, as in `"Employer's office"` or `'Employer\'s office'`.
export const STRING_LITERAL_SOURCE = String.raw`'(?:[^'\\\r\n]|\\.)*'|"(?:[^"\\\r\n]|\\.)*"`;

// What an escape sequence in a string literal stands for; any other escaped character stands for itself
const ESCAPE_SEQUENCE = /\\(u\{[0-9a-fA-F]+\}|u[0-9a-fA-F]{4}|x[0-9a-fA-F]{2}|[\s\S])/g;
const ESCAPED_CHARACTERS: Readonly<Record<string, string>> = {
  n: "\n",
  r: "\r",
  t: "\t",
  b: "\b",
  f: "\f",
  v: "\v",
  "0": "\0",
};
const HEX_RADIX = 16;

/** The value a string literal stands for: without its quotes, with its escape sequences resolved. */
export function decodeStringLiteral(literal: string): string {
  return literal.slice(1, -1).replace(ESCAPE_SEQUENCE, (_sequence: string, escaped: string) => decodeEscape(escaped));
}

function decodeEscape(escaped: string): string {
  if (escaped.startsWith("u{")) {
    return String.fromCodePoint(parseInt(escaped.slice(2, -1), HEX_RADIX));
  }

  // `uXXXX` and `xXX`; a lone `u` or `x` is one character and stands for itself
  if (escaped.length > 1) {
    return String.fromCharCode(parseInt(escaped.slice(1), HEX_RADIX));
  }

  return ESCAPED_CHARACTERS[escaped] ?? escaped;
}

/**
 * Role keys are free text (only surrounding spaces are rejected), and modelIds may hold an apostrophe or a backslash,
 * so a quote or backslash in one would end the literal early, and a line break would break it.
 */
export function toStringLiteral(value: string, quote: string): string {
  const escaped = value
    .replaceAll("\\", "\\\\")
    .replaceAll(quote, `\\${quote}`)
    .replaceAll("\n", "\\n")
    .replaceAll("\r", "\\r");
  return `${quote}${escaped}${quote}`;
}
